import * as THREE from "three";

/**
 * Camera Framing Utilities (Model-Viewer Approach)
 *
 * This module implements camera positioning based on @google/model-viewer's approach:
 * - Uses bounding sphere (not just bounding box) for accurate framing
 * - Calculates ideal camera distance using: radius / sin(halfFOV)
 * - Applies 105% padding by default (5% breathing room)
 * - Considers aspect ratio for optimal framing
 *
 * Key improvements over size-based multipliers:
 * - Mathematically precise based on geometry
 * - Scales correctly for any model size (small jewelry to large buildings)
 * - Consistent visual experience across different viewports
 */

/**
 * Content type identifiers for type-specific configurations
 */
export const ContentType = {
  AVATAR: "avatar",
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
  PDF: "pdf",
  MODEL: "model",
  GENERIC: "generic"
};

/**
 * Calculate bounding sphere from all vertices in a model
 * This is more accurate than using bounding box diagonal
 *
 * @param {THREE.Object3D} object - The 3D object to analyze
 * @param {THREE.Vector3} center - Center point for the sphere
 * @returns {{ center: THREE.Vector3, radius: number }}
 */
export const calculateBoundingSphere = (object, center) => {
  let maxDistanceSquared = 0;
  const vertex = new THREE.Vector3();

  object.traverse(child => {
    if (child.isMesh && child.geometry) {
      const positions = child.geometry.attributes.position;

      if (positions) {
        for (let i = 0; i < positions.count; i++) {
          vertex.fromBufferAttribute(positions, i);
          // Transform to world space
          vertex.applyMatrix4(child.matrixWorld);

          const distanceSquared = center.distanceToSquared(vertex);
          maxDistanceSquared = Math.max(maxDistanceSquared, distanceSquared);
        }
      }
    }
  });

  return {
    center: center.clone(),
    radius: Math.sqrt(maxDistanceSquared)
  };
};

/**
 * Calculate ideal camera distance based on bounding sphere and FOV
 * Formula from model-viewer: distance = radius / sin(halfFOV)
 *
 * Now considers both vertical and horizontal FOV to handle narrow viewports (mobile portrait)
 * Uses the larger distance to ensure the model fits in both dimensions
 *
 * @param {number} boundingSphereRadius - Radius of the bounding sphere
 * @param {number} fovDegrees - Camera vertical field of view in degrees
 * @param {number} aspect - Camera aspect ratio (width/height). If not provided, only vertical FOV is considered
 * @returns {number} - Ideal camera distance
 */
export const calculateIdealCameraDistance = (boundingSphereRadius, fovDegrees, aspect = null) => {
  const halfVertFovRad = (fovDegrees / 2) * (Math.PI / 180);
  const distanceForVertical = boundingSphereRadius / Math.sin(halfVertFovRad);

  // If aspect ratio is provided, also calculate distance needed for horizontal FOV
  // This is crucial for mobile portrait mode where horizontal FOV is much narrower
  if (aspect !== null && aspect > 0) {
    const halfHorizFovRad = Math.atan(Math.tan(halfVertFovRad) * aspect);
    const distanceForHorizontal = boundingSphereRadius / Math.sin(halfHorizFovRad);

    // Use the larger distance to ensure model fits in both dimensions
    return Math.max(distanceForVertical, distanceForHorizontal);
  }

  // Fallback to vertical-only calculation (backward compatibility)
  return distanceForVertical;
};

/**
 * Calculate adjusted FOV based on viewport aspect ratio
 * Ensures model stays fully visible when aspect ratio changes
 *
 * @param {number} baseFOV - Base field of view in degrees
 * @param {number} idealAspect - Ideal aspect ratio for the model
 * @param {number} viewportAspect - Current viewport aspect ratio
 * @returns {number} - Adjusted FOV in degrees
 */
export const calculateAdjustedFOV = (baseFOV, idealAspect, viewportAspect) => {
  const vertical = Math.tan(((baseFOV / 2) * Math.PI) / 180) * Math.max(1, idealAspect / viewportAspect);
  return (2 * Math.atan(vertical) * 180) / Math.PI;
};

/**
 * Calculate dynamic padding based on model size
 * Larger models get progressively more padding for better framing
 *
 * @param {number} boundingSphereRadius - Radius of the bounding sphere
 * @param {number} basePadding - Base padding from content config
 * @param {string} contentType - Type of content
 * @returns {number} - Adjusted padding
 */
export const calculateDynamicPadding = (boundingSphereRadius, basePadding, contentType) => {
  // Don't apply dynamic scaling to flat content (they're usually correct size)
  if (
    contentType === ContentType.IMAGE ||
    contentType === ContentType.VIDEO ||
    contentType === ContentType.PDF ||
    contentType === ContentType.AUDIO
  ) {
    return basePadding;
  }

  // For avatars and models: apply smart dynamic scaling
  // Normal human avatars (~1m radius) stay at base padding
  // Large models/avatars (>2m radius) get progressively more space

  if (contentType === ContentType.AVATAR) {
    // Avatars: More conservative thresholds
    // Normal human: radius ~0.9-1.2m → base padding (105%)
    // Large avatar/model: radius >2m → scale up
    if (boundingSphereRadius < 1.5) {
      return basePadding; // Normal human-sized avatars: 105%
    } else if (boundingSphereRadius < 3) {
      // Medium-large: gradual increase 105% → 130%
      const scale = 1.0 + ((boundingSphereRadius - 1.5) / 1.5) * 0.24;
      return basePadding * scale;
    } else if (boundingSphereRadius < 6) {
      // Large: 130% → 160%
      const scale = 1.24 + ((boundingSphereRadius - 3) / 3) * 0.29;
      return basePadding * scale;
    } else {
      // Very large: 160% → 190% (capped)
      const scale = Math.min(1.53 + ((boundingSphereRadius - 6) / 10) * 0.29, 1.81);
      return basePadding * scale;
    }
  }

  // For models/generic content - use model-viewer's flat approach (no dynamic scaling)
  // Model-viewer uses 105% for all models and it works well
  return basePadding; // Flat 105% like model-viewer
};

/**
 * Get content-specific camera configuration
 *
 * @param {string} contentType - Type of content being framed
 * @returns {{
 *   distancePadding: number,
 *   useOrbitAngle: boolean,
 *   orbitTheta: number,
 *   orbitPhi: number,
 *   centerOffsetY: number,
 *   idealAspect: number
 * }}
 */
export const getContentConfig = contentType => {
  switch (contentType) {
    case ContentType.AVATAR:
      return {
        distancePadding: 1.05, // 105% of ideal (5% breathing room)
        useOrbitAngle: true,
        orbitTheta: 0, // 0° horizontal rotation
        orbitPhi: 75, // 75° vertical angle (looking down slightly)
        centerOffsetY: 0.05, // Slight upward offset for better avatar framing
        idealAspect: 0.6 // Avatars tend to be taller than wide
      };

    case ContentType.IMAGE:
    case ContentType.VIDEO:
    case ContentType.PDF:
      return {
        distancePadding: 1.15, // 115% for flat content (more breathing room)
        useOrbitAngle: false,
        orbitTheta: 0,
        orbitPhi: 90, // Straight on view
        centerOffsetY: 0,
        idealAspect: 1.0 // Images can be any aspect
      };

    case ContentType.AUDIO:
      return {
        distancePadding: 1.2, // 120% for audio icon
        useOrbitAngle: false,
        orbitTheta: 0,
        orbitPhi: 90,
        centerOffsetY: 0,
        idealAspect: 1.0
      };

    case ContentType.MODEL:
    case ContentType.GENERIC:
    default:
      return {
        distancePadding: 1.05, // 105% - model-viewer default (same as avatars)
        useOrbitAngle: true,
        orbitTheta: 0,
        orbitPhi: 75,
        centerOffsetY: 0,
        idealAspect: 1.0
      };
  }
};

/**
 * Calculate optimal camera framing for an object (Model-Viewer approach)
 *
 * This is the main function that implements the model-viewer camera positioning strategy:
 * 1. Calculate bounding sphere from all vertices
 * 2. Compute ideal distance using: radius / sin(halfFOV)
 * 3. Apply content-specific padding (105% for avatars, etc.)
 * 4. Position camera using spherical coordinates
 * 5. Consider aspect ratio adjustments
 *
 * @param {THREE.Object3D} object - Object to frame
 * @param {THREE.Camera} camera - Camera to position
 * @param {Object} options - Configuration options
 * @param {string} options.contentType - Type of content (avatar, image, etc.)
 * @param {number} options.padding - Custom distance padding (overrides content type default)
 * @param {boolean} options.applyToCamera - Whether to apply position to camera
 * @param {number} options.customOrbitPhi - Custom vertical orbit angle (degrees)
 * @param {number} options.customOrbitTheta - Custom horizontal orbit angle (degrees)
 * @returns {{
 *   position: THREE.Vector3,
 *   target: THREE.Vector3,
 *   distance: number,
 *   boundingSphere: { center: THREE.Vector3, radius: number },
 *   adjustedFOV: number
 * }}
 */
export const calculateCameraFraming = (object, camera, options = {}) => {
  const {
    contentType = ContentType.GENERIC,
    padding = null,
    applyToCamera = false,
    customOrbitPhi = null,
    customOrbitTheta = null
  } = options;

  // Get content-specific configuration
  const config = getContentConfig(contentType);

  // 1. Calculate bounding box to get initial center
  const boundingBox = new THREE.Box3().setFromObject(object);
  const center = new THREE.Vector3();
  boundingBox.getCenter(center);

  // Apply center offset for better framing (e.g., avatars look better centered slightly higher)
  if (config.centerOffsetY !== 0) {
    center.y += config.centerOffsetY;
  }

  // 2. Calculate bounding sphere (more accurate than box diagonal)
  const boundingSphere = calculateBoundingSphere(object, center);

  // Handle edge case: very small or zero-size objects
  if (boundingSphere.radius < 0.001) {
    console.warn("Object has very small bounding sphere, using fallback distance");
    boundingSphere.radius = 0.5; // Fallback radius
  }

  // 3. Calculate ideal camera distance using model-viewer formula
  // Pass aspect ratio to ensure proper fitting on narrow viewports (mobile portrait)
  const viewportAspect = camera.aspect || 1;
  const idealDistance = calculateIdealCameraDistance(boundingSphere.radius, camera.fov, viewportAspect);

  // 4. Apply content-specific padding with dynamic scaling for large models
  let distancePadding = padding !== null ? padding : config.distancePadding;

  // Apply dynamic padding for large models (only if no custom padding provided)
  if (padding === null) {
    distancePadding = calculateDynamicPadding(boundingSphere.radius, distancePadding, contentType);
  }

  // 5. Add extra safety margin for mobile portrait mode (narrow viewports)
  // When aspect < 1, horizontal FOV is narrower and models can get cut off
  let mobilePortraitBoost = 1.0;
  if (viewportAspect < 1) {
    // Add 10-20% extra distance for portrait mode to prevent horizontal clipping
    // More boost for narrower viewports (aspect closer to 0)
    mobilePortraitBoost = 1.0 + (1.0 - viewportAspect) * 0.2;
  }

  const finalDistance = idealDistance * distancePadding * mobilePortraitBoost;

  // 6. Calculate camera position using spherical coordinates
  const orbitPhi = customOrbitPhi !== null ? customOrbitPhi : config.orbitPhi;
  const orbitTheta = customOrbitTheta !== null ? customOrbitTheta : config.orbitTheta;

  // Convert to radians
  const phiRad = (orbitPhi * Math.PI) / 180;
  const thetaRad = (orbitTheta * Math.PI) / 180;

  // Spherical to Cartesian conversion
  const cameraPosition = new THREE.Vector3(
    finalDistance * Math.sin(phiRad) * Math.sin(thetaRad),
    finalDistance * Math.cos(phiRad),
    finalDistance * Math.sin(phiRad) * Math.cos(thetaRad)
  );

  // Translate to object center
  cameraPosition.add(center);

  // 7. Calculate adjusted FOV for current viewport
  // (Note: viewportAspect already calculated above for idealDistance)
  const adjustedFOV = calculateAdjustedFOV(camera.fov, config.idealAspect, viewportAspect);

  // 8. Apply to camera if requested
  if (applyToCamera) {
    camera.position.copy(cameraPosition);
    camera.lookAt(center);
    camera.updateProjectionMatrix();
  }

  return {
    position: cameraPosition,
    target: center,
    distance: cameraPosition.distanceTo(center),
    boundingSphere,
    adjustedFOV
  };
};

/**
 * Calculate camera control bounds (min/max distance)
 * Based on bounding sphere size
 *
 * @param {THREE.Object3D} object - Object being viewed
 * @param {THREE.Camera} camera - Camera
 * @param {Object} options - Configuration options
 * @returns {{ minDistance: number, maxDistance: number }}
 */
export const calculateCameraBounds = (object, camera, options = {}) => {
  const { contentType = ContentType.GENERIC } = options;

  // Calculate bounding sphere
  const boundingBox = new THREE.Box3().setFromObject(object);
  const center = new THREE.Vector3();
  boundingBox.getCenter(center);
  const boundingSphere = calculateBoundingSphere(object, center);

  // Minimum distance: 20% of bounding sphere radius
  // This allows zooming in for details without clipping
  const minDistance = Math.max(0.1, boundingSphere.radius * 0.2);

  // Maximum distance: depends on content type
  // Must be high enough to allow the calculated padding to work
  let maxDistance;
  switch (contentType) {
    case ContentType.AVATAR:
      maxDistance = boundingSphere.radius * 8; // Avatars: 8x radius (for large avatar models)
      break;
    case ContentType.IMAGE:
    case ContentType.VIDEO:
    case ContentType.PDF:
      maxDistance = boundingSphere.radius * 5; // Flat content: 5x radius
      break;
    case ContentType.MODEL:
    case ContentType.GENERIC:
    default:
      // Models need higher max distance to allow proper framing
      // With 130% base + up to 1.6x multiplier, we can reach ~208% (2.08x radius)
      // So maxDistance should be at least 3x that for comfortable zooming
      maxDistance = boundingSphere.radius * 10; // Generic: 10x radius for comfortable zooming
  }

  return {
    minDistance,
    maxDistance: Math.max(minDistance * 2, maxDistance)
  };
};

/**
 * Quick camera reset using model-viewer approach
 * Convenience function for common use case
 *
 * @param {THREE.Object3D} object - Object to frame
 * @param {THREE.Camera} camera - Camera to position
 * @param {THREE.Controls} controls - Orbit controls (optional)
 * @param {string} contentType - Type of content
 */
export const resetCameraToObject = (object, camera, controls = null, contentType = ContentType.GENERIC) => {
  const result = calculateCameraFraming(object, camera, {
    contentType,
    applyToCamera: true
  });

  if (controls) {
    controls.target.copy(result.target);

    // Update control bounds BEFORE updating controls
    const bounds = calculateCameraBounds(object, camera, { contentType });
    controls.minDistance = bounds.minDistance;
    controls.maxDistance = bounds.maxDistance;

    // Update controls after setting bounds
    controls.update();
  }

  return result;
};

export default {
  calculateBoundingSphere,
  calculateIdealCameraDistance,
  calculateAdjustedFOV,
  calculateDynamicPadding,
  calculateCameraFraming,
  calculateCameraBounds,
  resetCameraToObject,
  getContentConfig,
  ContentType
};



